import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { DollarSign, ChevronLeft, Globe, Download, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmt(n, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(0)}`;
  }
}

export default function PayrollSalaryPage() {
  const navigate = useNavigate();
  const { timezone } = useReportFilters();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const startDate = `${monthStr}-01`;
  const endDate = (() => { const d = new Date(year, month + 1, 0); return `${monthStr}-${String(d.getDate()).padStart(2,'0')}`; })();
  useEffect(() => { load(); }, [monthStr]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payroll-reports/salary-calculation`, {
        params: { start_date: startDate, end_date: endDate }, withCredentials: true
      });
      setData(res.data);
    } catch { toast.error('Failed to load salary report'); }
    finally { setLoading(false); }
  };

  const rows = data?.salary_calculations || data?.records || [];
  const displayRows = rows.filter(r => r.salary_type !== 'not_configured');
  const currency = displayRows.find(r => r.currency && r.currency !== '–')?.currency || 'USD';
  const totalGross = displayRows.reduce((a, r) => a + (r.gross_salary || 0), 0);
  const totalNet = displayRows.reduce((a, r) => a + (r.net_salary || 0), 0);
  const totalDeductions = displayRows.reduce((a, r) => a + (r.deductions || r.total_deductions || 0), 0);
  const totalBonuses = displayRows.reduce((a, r) => a + (r.bonuses || r.total_bonuses || 0), 0);

  const chartData = displayRows.slice(0, 12).map(r => ({
    name: r.staff_name?.split(' ')[0],
    gross: Math.round(r.gross_salary || 0),
    net: Math.round(r.net_salary || 0),
    deductions: Math.round(r.deductions || r.total_deductions || 0),
  }));

  return (
    <div className="space-y-6" data-testid="payroll-salary-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Salary Calculation</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Year</Label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-8 text-sm w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[now.getFullYear()-1, now.getFullYear()].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Month</Label>
              <div className="flex flex-wrap gap-1">
                {MONTHS.map((m, i) => (
                  <button key={i} onClick={() => setMonth(i)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${i === month ? 'bg-violet-600 text-white border-violet-600' : 'text-gray-600 hover:bg-gray-100'}`}>{m}</button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading...</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Gross', value: fmt(totalGross, currency), Icon: DollarSign, c: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Total Net', value: fmt(totalNet, currency), Icon: ArrowUpRight, c: 'text-indigo-600', bg: 'bg-indigo-50' },
              { label: 'Total Deductions', value: fmt(totalDeductions, currency), Icon: ArrowDownRight, c: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Total Bonuses', value: fmt(totalBonuses, currency), Icon: ArrowUpRight, c: 'text-violet-600', bg: 'bg-violet-50' },
            ].map(({ label, value, Icon, c, bg }) => (
              <Card key={label} className="border-0 shadow-sm">
                <CardContent className={`pt-4 ${bg} rounded-xl flex gap-3 items-center`}>
                  <Icon className={`w-7 h-7 ${c}`} />
                  <div><p className={`text-xl font-bold ${c}`}>{value}</p><p className="text-xs text-gray-500">{label}</p></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {chartData.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Salary Breakdown — {MONTHS[month]} {year}</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => fmt(v, currency)} />
                    <Legend />
                    <Bar dataKey="gross" fill="#6366f1" name="Gross" radius={[3,3,0,0]} />
                    <Bar dataKey="net" fill="#22c55e" name="Net" radius={[3,3,0,0]} />
                    <Bar dataKey="deductions" fill="#ef4444" name="Deductions" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Salary Details ({displayRows.length} staff)</CardTitle>
                <Button size="sm" variant="outline" onClick={() => {
                  const csv = ['Staff,Dept,Base Pay,OT Pay,Bonuses,Deductions,Gross,Net,Paid Days,Status']
                    .concat(displayRows.map(r => `${r.staff_name},${r.department||'–'},${r.base_pay},${r.overtime_pay||0},${r.bonuses||r.total_bonuses||0},${r.deductions||r.total_deductions||0},${r.gross_salary},${r.net_salary},${r.paid_days||0},${r.payment_status||'pending'}`))
                    .join('\n');
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                  a.download = `salary-${monthStr}.csv`;
                  a.click();
                }}>
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>{['Staff','Dept','Base Pay','OT Pay','Bonuses','Deductions','Gross','Net','Status'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {displayRows.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-3 font-medium">{r.staff_name}</td>
                        <td className="px-3 py-3"><Badge variant="outline" className="text-xs">{r.department||'–'}</Badge></td>
                        <td className="px-3 py-3 text-gray-600">{fmt(r.base_pay, r.currency)}</td>
                        <td className="px-3 py-3 text-amber-600">{fmt(r.overtime_pay, r.currency)}</td>
                        <td className="px-3 py-3 text-indigo-600">{fmt(r.bonuses || r.total_bonuses, r.currency)}</td>
                        <td className="px-3 py-3 text-red-600">-{fmt(r.deductions || r.total_deductions, r.currency)}</td>
                        <td className="px-3 py-3 text-blue-700 font-semibold">{fmt(r.gross_salary, r.currency)}</td>
                        <td className="px-3 py-3 text-indigo-700 font-bold">{fmt(r.net_salary, r.currency)}</td>
                        <td className="px-3 py-3">
                          <Badge className={r.payment_status === 'paid' ? 'bg-indigo-100 text-indigo-700' : r.payment_status === 'processing' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>
                            {r.payment_status || 'pending'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {displayRows.length > 0 && (
                    <tfoot className="bg-gray-50 border-t-2 font-semibold">
                      <tr>
                        <td className="px-3 py-3 font-bold" colSpan={2}>TOTAL ({displayRows.length} staff)</td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3 text-amber-600">{fmt(displayRows.reduce((a,r)=>a+(r.overtime_pay||0),0), currency)}</td>
                        <td className="px-3 py-3 text-indigo-600">{fmt(totalBonuses, currency)}</td>
                        <td className="px-3 py-3 text-red-600">-{fmt(totalDeductions, currency)}</td>
                        <td className="px-3 py-3 text-blue-700">{fmt(totalGross, currency)}</td>
                        <td className="px-3 py-3 text-indigo-700">{fmt(totalNet, currency)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
